// Lector de Markdown de la base de conocimiento (Manual): titulos con ancla e indice, pasos numerados, codigo con
// «Copiar», tablas, enlaces, imagenes, avisos (Ojo / Consejo / Importante), adjuntos recuperados de Zammad y botones
// «Abrir acceso». Todo se arma con elementos de React a partir de lib/markdownKB: no se inserta HTML del articulo.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { archivoConSesion } from '../api/http';
import { Icon } from '../lib/icons';
import { type Bloque, bloquesMarkdown, type EnLinea, indice as indiceDe } from '../lib/markdownKB';
import { toast } from './toasts';

export const urlAdjunto = (id: string) => `/correo/api/conocimiento/adjunto/${encodeURIComponent(id)}`;
export const urlAcceso = (id: string) => `/bandeja/accesos?acceso=${encodeURIComponent(id)}`;
const ES_IMAGEN = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const ID_TITULO = (id: string) => `kb-${id}`;

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

// Abre un adjunto del bucket con la sesion (un enlace normal no lleva el token).
async function abrirAdjunto(id: string, nombre: string) {
  const ventana = window.open('', '_blank');
  try {
    const b = await archivoConSesion(urlAdjunto(id));
    const url = URL.createObjectURL(b);
    if (ventana) ventana.location.href = url;
    else {
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    ventana?.close();
    toast((e as Error).message || 'No se pudo abrir el adjunto.', 'error');
  }
}

function ImagenAdjunta({ id, nombre }: { id: string; nombre: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);
  useEffect(() => {
    let vivo = true;
    let u = '';
    archivoConSesion(urlAdjunto(id))
      .then((b) => {
        u = URL.createObjectURL(b);
        if (vivo) setUrl(u);
      })
      .catch(() => vivo && setFallo(true));
    return () => {
      vivo = false;
      if (u) URL.revokeObjectURL(u);
    };
  }, [id]);
  if (fallo || !url) return <ChipAdjunto id={id} nombre={nombre} cargando={!fallo} />;
  return (
    <button type="button" className="md-img-adj" onClick={() => void abrirAdjunto(id, nombre)} title={`Abrir ${nombre}`}>
      <img src={url} alt={nombre} loading="lazy" />
    </button>
  );
}

function ChipAdjunto({ id, nombre, cargando }: { id: string; nombre: string; cargando?: boolean }) {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(nombre)?.[1]?.toUpperCase();
  return (
    <a
      className="md-adjunto"
      href={urlAdjunto(id)}
      onClick={(e) => {
        e.preventDefault();
        void abrirAdjunto(id, nombre);
      }}
      aria-busy={cargando || undefined}
    >
      <span className="md-adjunto-i">
        <Icon name={ES_IMAGEN.test(nombre) ? 'image' : 'paperclip'} size={14} />
      </span>
      <span className="md-adjunto-n">{nombre}</span>
      {ext ? <small>{ext}</small> : null}
    </a>
  );
}

function BotonAcceso({ id, titulo }: { id: string; titulo: string }) {
  const nav = useNavigate();
  return (
    <a
      className="md-acceso"
      href={urlAcceso(id)}
      onClick={(e) => {
        e.preventDefault();
        nav(urlAcceso(id));
      }}
    >
      <Icon name="lock" size={14} />
      Abrir acceso «{titulo}»
    </a>
  );
}

function enLinea(xs: EnLinea[], k = 'x'): ReactNode[] {
  return xs.map((x, i) => {
    const key = `${k}-${i}`;
    switch (x.t) {
      case 'texto':
        return x.v;
      case 'salto':
        return <br key={key} />;
      case 'negrita':
        return <b key={key}>{enLinea(x.hijos, key)}</b>;
      case 'cursiva':
        return <em key={key}>{enLinea(x.hijos, key)}</em>;
      case 'codigo':
        return <code key={key}>{x.v}</code>;
      case 'enlace':
        return x.href.startsWith('/') ? (
          <Link key={key} to={x.href}>
            {enLinea(x.hijos, key)}
          </Link>
        ) : x.href.startsWith('#') ? (
          <a key={key} href={`#${ID_TITULO(x.href.slice(1))}`}>
            {enLinea(x.hijos, key)}
          </a>
        ) : (
          <a key={key} href={x.href} target="_blank" rel="noreferrer noopener">
            {enLinea(x.hijos, key)}
          </a>
        );
      case 'imagen':
        return <img key={key} className="md-img" src={x.src} alt={x.alt} loading="lazy" referrerPolicy="no-referrer" />;
      case 'adjunto':
        return ES_IMAGEN.test(x.nombre) ? <ImagenAdjunta key={key} id={x.id} nombre={x.nombre} /> : <ChipAdjunto key={key} id={x.id} nombre={x.nombre} />;
      case 'acceso':
        return <BotonAcceso key={key} id={x.id} titulo={x.titulo} />;
    }
  });
}

function Codigo({ texto, lenguaje }: { texto: string; lenguaje: string }) {
  const [hecho, setHecho] = useState(false);
  return (
    <div className="md-code">
      <div className="md-code-h">
        <span>{lenguaje || 'código'}</span>
        <button
          type="button"
          onClick={async () => {
            if (await copiar(texto)) {
              setHecho(true);
              setTimeout(() => setHecho(false), 1600);
            } else toast('No se pudo copiar: selecciona el texto y usa Ctrl+C.', 'error');
          }}
        >
          <Icon name={hecho ? 'check' : 'copy'} size={13} /> {hecho ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <pre>
        <code>{texto}</code>
      </pre>
    </div>
  );
}

const NOMBRE_AVISO = { ojo: 'Ojo', consejo: 'Consejo', importante: 'Importante' } as const;
const ICONO_AVISO = { ojo: 'alert', consejo: 'spark', importante: 'info' } as const;

function bloques(bs: Bloque[], k = 'b'): ReactNode[] {
  return bs.map((b, i) => {
    const key = `${k}-${i}`;
    switch (b.t) {
      case 'titulo': {
        const H = `h${Math.min(6, b.nivel + 1)}` as 'h2';
        return (
          <H key={key} id={ID_TITULO(b.id)} className="md-h">
            {enLinea(b.hijos, key)}
            <a className="md-ancla" href={`#${ID_TITULO(b.id)}`} aria-label={`Enlace a «${b.texto}»`}>
              #
            </a>
          </H>
        );
      }
      case 'parrafo':
        return <p key={key}>{enLinea(b.hijos, key)}</p>;
      case 'lista':
        return (
          <ul key={key}>
            {b.items.map((it, j) => (
              <li key={j}>{bloques(it, `${key}-${j}`)}</li>
            ))}
          </ul>
        );
      case 'pasos':
        return (
          <ol key={key} className="md-pasos" start={b.inicio}>
            {b.items.map((it, j) => (
              <li key={j}>
                <span className="md-paso-n" aria-hidden="true">
                  {b.inicio + j}
                </span>
                <div className="md-paso-c">
                  {it.titulo ? <b className="md-paso-t">{enLinea(it.titulo, `${key}-${j}-t`)}</b> : null}
                  {bloques(it.cuerpo, `${key}-${j}`)}
                </div>
              </li>
            ))}
          </ol>
        );
      case 'codigo':
        return <Codigo key={key} texto={b.texto} lenguaje={b.lenguaje} />;
      case 'tabla':
        return (
          <div key={key} className="md-tabla" role="region" aria-label="Tabla" tabIndex={0}>
            <table>
              <thead>
                <tr>
                  {b.encabezado.map((c, j) => (
                    <th key={j} style={{ textAlign: alinear(b.alineacion[j]) }}>
                      {enLinea(c, `${key}-h${j}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.filas.map((f, r) => (
                  <tr key={r}>
                    {b.encabezado.map((_, j) => (
                      <td key={j} style={{ textAlign: alinear(b.alineacion[j]) }}>
                        {enLinea(f[j] ?? [], `${key}-${r}-${j}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'aviso':
        return (
          <div key={key} className={`md-aviso md-aviso-${b.tipo}`} role="note">
            <b className="md-aviso-t">
              <Icon name={ICONO_AVISO[b.tipo]} size={15} /> {NOMBRE_AVISO[b.tipo]}
            </b>
            <div>{bloques(b.cuerpo, key)}</div>
          </div>
        );
      case 'cita':
        return <blockquote key={key}>{bloques(b.cuerpo, key)}</blockquote>;
      case 'regla':
        return <hr key={key} />;
    }
  });
}

const alinear = (a: 'izq' | 'centro' | 'der' | null) => (a === 'centro' ? 'center' : a === 'der' ? 'right' : undefined);

export function Markdown({ texto, conIndice = true }: { texto: string; conIndice?: boolean }) {
  const bs = useMemo(() => bloquesMarkdown(texto), [texto]);
  const idx = useMemo(() => indiceDe(bs), [bs]);
  return (
    <div className="md">
      {conIndice && idx.length >= 3 ? (
        <nav className="md-indice" aria-label="En este artículo">
          <b>En este artículo</b>
          <ol>
            {idx.map((x) => (
              <li key={x.id} style={{ paddingLeft: (x.nivel - 1) * 12 }}>
                <a
                  href={`#${ID_TITULO(x.id)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(ID_TITULO(x.id))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  {x.texto}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      {bloques(bs)}
    </div>
  );
}
